import { useEffect, useState } from "react";
import { supabase } from "./backend/supabase";
import { Routes, Route, useNavigationType, useLocation, useNavigate, Navigate, useParams } from "react-router-dom";
import { ConfigProvider } from "./utils/config";
import { CreditsProvider } from "./pages/pricing/CreditsContext";
import { Helmet } from 'react-helmet-async';
import posthog from './utils/analytics';
import { useConfig, defaultConfig } from "./utils/config";
import LoadingSpinner from "./ui/components/LoadingSpinner";
import Game from "./pages/game/Game";
import Play from "./pages/game/Play";
import TestPlay from "./pages/game/TestPlay";
import Art from "./pages/art/Art";
import Talk from "./pages/talk/Talk";
import Login from "./pages/account/Login";
import SignUp from "./pages/account/SignUp";
import ForgotPassword from "./pages/account/ForgotPassword";
import ResetPassword from "./pages/account/ResetPassword";
import Profile from "./pages/account/Profile";
import Home from "./pages/game/Home";
import Watch from "./pages/website/Watch";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import Animate from "./pages/animate/Animate";
import Sound from "./pages/sound/Sound";
import GithubAccountConnected from "./pages/game/components/integrations/GithubAccountConnected";
import Test from "./pages/game/components/stream/internal/Test";
import Pricing from "./pages/pricing/Pricing";
import Debug from "./pages/debug/Debug";
import { getWebsiteUrl } from "./utils/urls";
import RemixAction from "./pages/game/Remix";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        // console.log('Auth check:', session ? 'User authenticated' : 'User not authenticated');
        setAuthenticated(!!session);
      } catch (error) {
        // console.log('Auth check error:', error);
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // console.log('Auth state changed:', event);
      setAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (loading) {
    // console.log('Loading');
    return <LoadingSpinner />;
  }

  if (defaultConfig.REDIRECT_TO_WEBSITE) {
    localStorage.setItem('redirectPath', location.pathname + location.search);
  }
  return authenticated ? <>{children}</> : <Navigate to="/login" state={{ from: location }} replace />;
};

function App() {
  // console.log('[APP] Env:', {
  //   NODE_ENV: process.env.NODE_ENV,
  //   ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
  //   REACT_APP_FRONTEND_WEBSITE_URL: process.env.REACT_APP_FRONTEND_WEBSITE_URL,
  // });

  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const config = useConfig();

  // Track page views and leaves
  useEffect(() => {
    // Capture pageview when route changes
    posthog.capture('$pageview', {
      path: pathname,
      title: document.title
    });

    // Capture pageleave on component unmount
    return () => {
      posthog.capture('$pageleave', {
        path: pathname,
        title: document.title
      });
    };
  }, [pathname]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Identify user in PostHog when session exists
        posthog.identify(
          session.user.id,
          { email: session.user.email }
        );
        
        if (location.pathname === '/') {
          navigate('/game', { replace: true });
        }
      } else {
        if (location.pathname === '/' && config.REDIRECT_TO_WEBSITE) {
          window.location.href = getWebsiteUrl('/');
        }
      }
    };

    checkAuth();
  }, [location, navigate, posthog, config]);

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  const info = [
    {
      route: '/login',
      title: 'Log In',
      description: 'Log into your playmix.ai account.',
    },
    {
      route: '/signup',
      title: 'Sign Up',
      description: 'Sign up for a free playmix.ai account.',
    },
    {
      route: '/forgot-password',
      title: 'Forgot Password',
      description: 'Request a password reset for your playmix.ai account.',
    },
    {
      route: '/reset-password',
      title: 'New Password',
      description: 'Set a new playmix.ai password.',
    },
    {
      route: '/profile',
      title: 'Profile',
      description: 'Profile',
    },
    {
      route: '/privacy-policy',
      title: 'Privacy Policy',
      description: 'Privacy policy for playmix.ai.',
    },
    {
      route: '/terms-of-service',
      title: 'Terms of Service',
      description: 'Terms of Service for playmix.ai.',
    },
    {
      route: '/pricing',
      title: 'Pricing',
      description: 'Pricing, usage, and credits for playmix.ai.',
    },
    {
      route: '/',
      title: 'playmix.ai',
      description: 'AI Game Maker. Create playable games in seconds with AI.',
    },
    {
      route: '/watch',
      title: 'playmix.ai video',
      description: 'Turn any idea into a playable game in seconds with playmix.ai.',
    },
    {
      route: '/game',
      title: 'playmix.ai',
      description: 'Vibe create games with AI. Or play community games.',
    },
    {
      route: '/play',
      title: 'Play',
      description: 'Play games. Share with friends.',
    },
    {
      route: '/test',
      title: 'Test',
      description: 'Test games.',
    },
    {
      route: '/art',
      title: 'Art',
      description: 'Sketch to 2D and 3D art with AI.',
    },
    {
      route: '/3d',
      title: '3D Art',
      description: '3D Art',
    },
    {
      route: '/animate',
      title: 'Animate',
      description: 'Create game-ready sprite sheets in any style.',
    },
    {
      route: '/sound',
      title: 'Sound',
      description: 'Sound',
    },
    {
      route: '/talk',
      title: 'Talk',
      description: 'Talk',
    },
    {
      route: '/github-account/callback',
      title: 'Github Account',
      description: 'Github Account Connected',
    },
    {
      route: '/debug',
      title: 'Debug',
      description: 'Internal Debug',
    }
  ];
  
  const currentRoute = pathname || '/';
  const currentInfo = info.find(item => 
    item.route === currentRoute || 
    (currentRoute.startsWith(item.route + '/') && item.route !== '/')
  ) || info[0];

  return (
    <ConfigProvider>
      <CreditsProvider>
        <Helmet defaultTitle={currentInfo.title} titleTemplate="%s">
          <title>{currentInfo.title}</title>
          <meta name="description" content={currentInfo.description} />
          <link rel="canonical" href="https://playmix.ai" />
        </Helmet>
      <Routes>
        <Route path="/login" element={<Login supabase={supabase} />} />
        <Route path="/signup" element={<SignUp supabase={supabase} />} />
        <Route path="/forgot-password" element={<ForgotPassword supabase={supabase} />} />
        <Route path="/reset-password" element={<ResetPassword supabase={supabase} />} />
        <Route path="/auth/confirm" element={<Login supabase={supabase} />} />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/pricing" element={<Pricing />} />
        {config.REDIRECT_TO_WEBSITE ? 
          <Route path="/" element={<LoadingSpinner colorMode="dark" />} />
        : 
          <Route path="/" element={<Home />} />
        }
        {!config.REDIRECT_TO_WEBSITE && 
          <Route path="/watch" element={<Watch />} />
        }
        <Route 
          path="/game" 
          element={
            <ProtectedRoute>
              <Game />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/game/:gameId" 
          element={
            <ProtectedRoute>
              <Game />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/remix/:gameId" 
          element={
            <ProtectedRoute>
              <RemixAction />
            </ProtectedRoute>
          } 
        />
        {!config.REDIRECT_TO_WEBSITE &&
          <Route path="/play/:gameId" element={<Play />} />
        }
        <Route path="/test/:gameId" element={<TestPlay />} />
        {config.REDIRECT_TO_WEBSITE ? 
          <Route 
            path="/art" 
            element={
              <ProtectedRoute>
                <Art />
              </ProtectedRoute>
            } 
          />
        : 
          <Route path="/art" element={<Art />} />
        }
        <Route 
          path="/art/:artId" 
          element={
            <ProtectedRoute>
              <Art />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/3d/:artId" 
          element={
            <ProtectedRoute>
              <Art />
            </ProtectedRoute>
          } 
        />
        {config.ENABLE_ANIMATION && (
          <>
          {config.REDIRECT_TO_WEBSITE ? 
            <Route 
            path="/animate" 
            element={
              <ProtectedRoute>
                <Animate />
              </ProtectedRoute>
            } 
          />
          : 
            <Route path="/animate" element={<Animate />} />
          }
          <Route 
            path="/animate/:animationId" 
            element={
              <ProtectedRoute>
                <Animate />
              </ProtectedRoute>
            } 
          />
          </>
        )}
        {config.ENABLE_SOUND && (
          <Route path="/sound" element={<Sound />} />
        )}
        {config.STREAM_TEST_PAGE && (
          <Route 
            path="/test" 
            element={
              <ProtectedRoute>
                <Test />
              </ProtectedRoute>
            } 
          />
        )}
        {/* <Route path="/talk" element={<Talk />} /> */}
        <Route path="/github-account/callback" element={<GithubAccountConnected />} />
        <Route 
          path="/debug" 
          element={
            <ProtectedRoute>
              <Debug />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </CreditsProvider>
    </ConfigProvider>
  );
}

export default App;